import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'phosphor-react';
import { Capacitor } from '@capacitor/core';
import { GoldenRule } from '@/components/ui/GoldenRule';
import { supabase } from '@/lib/supabase';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { isSafeHttpUrl } from '@/lib/url';

type SourceRow = {
  id: string; name: string; publisher: string | null; license: string;
  homepage: string | null; attribution: string | null;
  enabled: boolean; last_run_at: string | null; last_status: string | null;
};

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="mt-3 space-y-2">
      {items.map((item, i) => (
        <li key={i} className="t-ui text-ink-2 flex gap-2">
          <span className="shrink-0 select-none text-ink-3">—</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function DataBlocks2({ rows }: { rows: { label: string; value: string }[] }) {
  return (
    <div className="mt-4 rounded-lg ring-1 ring-paper-edge overflow-hidden divide-y divide-paper-edge">
      {rows.map((row, i) => (
        <div key={i} className="px-4 py-3 flex flex-col gap-0.5">
          <span className="t-ui font-medium text-ink-1">{row.label}</span>
          <span className="t-ui-sm text-ink-2">{row.value}</span>
        </div>
      ))}
    </div>
  );
}

function DataBlocks3({ rows }: { rows: { col1: string; col2: string; col3: string }[] }) {
  return (
    <div className="mt-4 rounded-lg ring-1 ring-paper-edge overflow-hidden divide-y divide-paper-edge">
      {rows.map((row, i) => (
        <div key={i} className="px-4 py-3 flex flex-col gap-0.5">
          <span className="t-ui font-medium text-ink-1">{row.col1}</span>
          <span className="t-ui-sm text-ink-2">{row.col2}</span>
          <span className="t-mono-xs text-ink-3">{row.col3}</span>
        </div>
      ))}
    </div>
  );
}

function DataBlocks4({ rows }: { rows: { col1: string; col2: string; col3: string; col4: string }[] }) {
  return (
    <div className="mt-4 rounded-lg ring-1 ring-paper-edge overflow-hidden divide-y divide-paper-edge">
      {rows.map((row, i) => (
        <div key={i} className="px-4 py-3 flex flex-col gap-0.5">
          <span className="t-ui font-medium text-ink-1">{row.col1}</span>
          <span className="t-ui-sm text-ink-2">{row.col2} · {row.col3}</span>
          <span className="t-mono-xs text-ink-3">{row.col4}</span>
        </div>
      ))}
    </div>
  );
}

function ArticleTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="mt-6 mb-2 font-serif italic text-[1.05rem] leading-snug text-ink-1">{children}</h3>;
}

export default function LegalPublic() {
  const nav = useNavigate();
  const { hash } = useLocation();
  const [sources, setSources] = useState<SourceRow[]>([]);
  // La liste des sources d'événements n'est affichée que sur le web : on la masque
  // dans l'app native (Android/iOS) — et on évite la requête correspondante.
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    supabase.from('data_sources').select('*').order('name').then(({ data }) => {
      if (data) setSources(data as SourceRow[]);
    });
  }, []);

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace('#', '');
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, [hash]);

  return (
    <div className="bg-paper text-ink-1 min-h-screen pb-tabbar">
      <header className="px-5 pt-[max(env(safe-area-inset-top),12px)] pb-3 flex items-center gap-3">
        <button onClick={() => nav(-1)} className="h-9 w-9 grid place-items-center rounded-full bg-paper-elev ring-1 ring-paper-edge"><ArrowLeft size={16} /></button>
        <span className="t-ed-lg">Mentions, données, sources</span>
      </header>

      {/* ─── Section 1 — Mentions légales ─── */}
      <section id="mentions-legales" className="px-5 mb-10 scroll-mt-4">
        <div className="t-cap mb-2">Mentions légales</div>
        <h2 className="t-ed-xl mb-3">L'<em className="italic-keyword">éditeur</em></h2>
        <GoldenRule short />

        <div className="mt-4 space-y-5 t-ui text-ink-2">
          <div>
            <p className="font-medium text-ink-1 mb-1">Éditeur du service</p>
            <p>Gazette est édité par Jeremy Montesinos, entrepreneur individuel.</p>
            <p>SIRET : 947&nbsp;836&nbsp;383&nbsp;00010</p>
            <p>Adresse : communiquée sur demande légitime.</p>
            <p>Directeur de la publication : Jeremy Montesinos</p>
            <p>Contact : <a href="mailto:contact@gazette.fr" className="underline">contact@gazette.fr</a></p>
          </div>

          <div>
            <p className="font-medium text-ink-1 mb-1">Hébergement — Application web</p>
            <p>Vercel Inc.</p>
            <p>340 S Lemon Ave #4133, Walnut, CA 91789, États-Unis</p>
            <p><a href="https://vercel.com" target="_blank" rel="noreferrer" className="underline">vercel.com</a></p>
          </div>

          <div>
            <p className="font-medium text-ink-1 mb-1">Hébergement — Base de données et backend</p>
            <p>Supabase Inc.</p>
            <p>970 Toa Payoh North #07-04, Singapore 318992</p>
            <p>Région du projet : eu-central-1 (Francfort, Allemagne, Union européenne)</p>
            <p><a href="https://supabase.com" target="_blank" rel="noreferrer" className="underline">supabase.com</a></p>
          </div>

          <div>
            <p className="font-medium text-ink-1 mb-1">Propriété intellectuelle</p>
            <p>La marque Gazette, le logo et l'ensemble des éléments graphiques de l'application sont la propriété de l'éditeur. Toute reproduction est interdite sans autorisation écrite.</p>
            <p className="mt-2">Les données événementielles affichées proviennent de sources publiques ouvertes et sont diffusées sous leurs licences respectives (Licence Ouverte Etalab 2.0, ODbL 1.0, CC BY 4.0). L'attribution de chaque source est indiquée dans la page « Sources » ci-dessous.</p>
          </div>

          <div>
            <p className="font-medium text-ink-1 mb-1">Crédits</p>
            <p>Typographies : Instrument Serif (Google Fonts, OFL), Geist Sans et Geist Mono (Vercel, OFL).</p>
            <p>Icônes : Phosphor Icons (MIT).</p>
            <p>Cartographie : © OpenStreetMap contributors, tuiles CartoDB (CC BY 3.0).</p>
          </div>
        </div>
      </section>

      {/* ─── Section 2 — Sources ─── */}
      <section className="px-5 mb-10">
        <div className="t-cap mb-2">Sources</div>
        <h2 className="t-ed-xl mb-3">D'où viennent les <em className="italic-keyword">événements</em> ?</h2>
        <GoldenRule short />
        <p className="mt-4 t-ui text-ink-2">
          Les sorties que vous voyez proviennent de sources publiques et ouvertes : <strong>DATAtourisme</strong> (réseau ADN Tourisme),
          <strong> OpenAgenda</strong>, des flux ICS/RSS d'organisateurs partenaires, et des contributions locales.
          Toutes les données sont diffusées sous licence Etalab 2.0 ou compatible (CC BY 4.0).
          Nous ne pratiquons pas de scraping et nous ne contournons aucune CGU.
        </p>

        {!isNative && sources.length > 0 && (
          <div className="mt-5 rounded-lg ring-1 ring-paper-edge overflow-hidden">
            {sources.map((s) => (
              <a
                key={s.id}
                href={isSafeHttpUrl(s.homepage) ? s.homepage! : '#'}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between gap-3 px-4 py-3 border-b border-paper-edge last:border-b-0 hover:bg-paper-elev"
              >
                <div className="min-w-0">
                  <div className="t-ui font-medium truncate">{s.name}</div>
                  <div className="t-mono-xs text-ink-3 truncate">{s.license}{s.publisher ? ` · ${s.publisher}` : ''}</div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className={`t-cap px-2 py-0.5 rounded-full ring-1 ${s.last_status === 'ok' ? 'ring-green-600/30 text-green-700' : s.last_status === 'error' ? 'ring-red-600/30 text-red-700' : 'ring-paper-edge text-ink-3'}`}>
                    {s.last_status ?? 'en attente'}
                  </span>
                  {s.last_run_at && (
                    <span className="t-mono-xs text-ink-3">MAJ {new Date(s.last_run_at).toLocaleDateString('fr-FR')}</span>
                  )}
                </div>
              </a>
            ))}
          </div>
        )}
      </section>

      {/* ─── Section 3 — CGU ─── */}
      <section id="cgu" className="px-5 mb-10 scroll-mt-4">
        <div className="t-cap mb-2">CGU</div>
        <h2 className="t-ed-xl mb-3">Conditions <em className="italic-keyword">d'utilisation</em></h2>
        <GoldenRule short />
        <p className="mt-3 t-mono-xs text-ink-3">En vigueur au 1er mai 2026.</p>

        <ArticleTitle>Article 1 — Objet</ArticleTitle>
        <p className="t-ui text-ink-2">Les présentes Conditions Générales d'Utilisation régissent l'accès et l'utilisation de l'application mobile et web Gazette (ci-après « le Service »), éditée par Jeremy Montesinos (ci-après « l'Éditeur »). L'utilisation du Service implique l'acceptation pleine et entière des présentes CGU. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser le Service.</p>

        <ArticleTitle>Article 2 — Description du Service</ArticleTitle>
        <p className="t-ui text-ink-2">Gazette est un service gratuit d'agrégation et de consultation d'événements publics en France métropolitaine. Il centralise des informations issues de sources ouvertes et légales (DATAtourisme, OpenAgenda, portails open data des collectivités, flux ICS d'organisateurs partenaires) dans une interface unifiée.</p>
        <p className="mt-2 t-ui text-ink-2">Le Service permet de découvrir des événements à proximité, de les enregistrer en favoris, de créer des alertes personnalisées et de consulter un agenda personnel.</p>
        <p className="mt-2 t-ui text-ink-2">Gazette n'est pas un service de billetterie et ne permet pas l'achat de billets ni l'inscription à des événements. Le Service redirige vers les sites officiels des organisateurs pour ces démarches.</p>

        <ArticleTitle>Article 3 — Accès au Service</ArticleTitle>
        <p className="t-ui text-ink-2">La consultation des événements (pages « À découvrir », « Explorer », fiches événement, recherche) est accessible sans création de compte.</p>
        <p className="mt-2 t-ui text-ink-2">La création d'un compte (via adresse email ou connexion Google) est nécessaire pour accéder aux fonctionnalités personnalisées : favoris, alertes, agenda personnel, abonnements.</p>
        <p className="mt-2 t-ui text-ink-2">L'Éditeur se réserve le droit de suspendre ou d'interrompre le Service à tout moment, sans préavis ni indemnité, notamment pour des raisons de maintenance ou de mise à jour.</p>

        <ArticleTitle>Article 4 — Fiabilité des informations</ArticleTitle>
        <p className="t-ui text-ink-2">Les informations relatives aux événements (dates, horaires, lieux, tarifs, descriptions) sont collectées automatiquement depuis des sources tierces ouvertes. L'Éditeur ne les saisit pas manuellement et ne peut garantir leur exactitude, leur exhaustivité ni leur mise à jour en temps réel.</p>
        <p className="mt-2 t-ui text-ink-2">Un avertissement est affiché sur chaque fiche événement invitant l'utilisateur à vérifier les informations auprès de l'organisateur ou de la source officielle avant de se déplacer.</p>
        <p className="mt-2 t-ui text-ink-2">L'Éditeur ne saurait être tenu responsable des erreurs, omissions, annulations ou modifications d'événements par leurs organisateurs respectifs.</p>

        <ArticleTitle>Article 5 — Obligations de l'utilisateur</ArticleTitle>
        <p className="t-ui text-ink-2">L'utilisateur s'engage à utiliser le Service conformément à sa destination et aux présentes CGU. Il s'engage notamment à ne pas :</p>
        <BulletList items={[
          'tenter d\'extraire, copier ou réutiliser de manière systématique tout ou partie de la base de données du Service (conformément aux articles L. 342-1 et suivants du Code de la propriété intellectuelle relatifs au droit sui generis des bases de données) ;',
          'utiliser des robots, scripts ou tout procédé automatisé pour accéder au Service ou en extraire des données ;',
          'perturber le fonctionnement du Service ou de son infrastructure ;',
          'usurper l\'identité d\'un tiers.',
        ]} />

        <ArticleTitle>Article 6 — Signalement d'erreurs</ArticleTitle>
        <p className="t-ui text-ink-2">Un mécanisme de signalement (« Une erreur ? Prévenez-nous ») est accessible sur chaque fiche événement. L'Éditeur s'engage à traiter les signalements dans un délai raisonnable, sans garantie de délai.</p>
        <p className="mt-2 t-ui text-ink-2">Tout organisateur souhaitant la modification ou le retrait d'un événement le concernant peut contacter l'Éditeur à <a href="mailto:contact@gazette.fr" className="underline">contact@gazette.fr</a>. L'Éditeur s'engage à donner suite dans un délai de 72 heures ouvrées.</p>

        <ArticleTitle>Article 7 — Propriété intellectuelle</ArticleTitle>
        <p className="t-ui text-ink-2">Le Service, sa structure, son design, ses textes éditoriaux et ses éléments graphiques sont protégés par le droit d'auteur et appartiennent à l'Éditeur.</p>
        <p className="mt-2 t-ui text-ink-2">Les données événementielles proviennent de sources tierces et sont diffusées sous leurs licences ouvertes respectives. Leur utilisation par l'utilisateur est soumise aux termes de ces licences.</p>

        <ArticleTitle>Article 8 — Limitation de responsabilité</ArticleTitle>
        <p className="t-ui text-ink-2">Le Service est fourni « en l'état ». L'Éditeur ne garantit pas l'absence d'interruptions, d'erreurs techniques ou de failles de sécurité.</p>
        <p className="mt-2 t-ui text-ink-2">L'Éditeur ne saurait être tenu responsable des dommages directs ou indirects résultant de l'utilisation du Service, notamment en cas de déplacement inutile lié à un événement annulé, modifié ou erroné.</p>

        <ArticleTitle>Article 9 — Liens vers des sites tiers</ArticleTitle>
        <p className="t-ui text-ink-2">Le Service contient des liens hypertextes vers les sites officiels des organisateurs d'événements et des sources de données. L'Éditeur n'exerce aucun contrôle sur ces sites et décline toute responsabilité quant à leur contenu.</p>

        <ArticleTitle>Article 10 — Modification des CGU</ArticleTitle>
        <p className="t-ui text-ink-2">L'Éditeur se réserve le droit de modifier les présentes CGU à tout moment. Les modifications prennent effet dès leur publication dans l'application. La continuation de l'utilisation du Service après modification vaut acceptation des nouvelles CGU.</p>

        <ArticleTitle>Article 11 — Droit applicable et litiges</ArticleTitle>
        <p className="t-ui text-ink-2">Les présentes CGU sont soumises au droit français. En cas de litige, les parties s'engagent à rechercher une solution amiable. À défaut, les tribunaux compétents du ressort du siège de l'Éditeur seront seuls compétents.</p>
        <p className="mt-2 t-ui text-ink-2">Pour les consommateurs résidant dans l'Union européenne : conformément aux dispositions du Code de la consommation relatives au règlement amiable des litiges, vous pouvez recourir à la plateforme de règlement en ligne des litiges de la Commission européenne accessible à l'adresse : <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer" className="underline">ec.europa.eu/consumers/odr</a>.</p>
      </section>

      {/* ─── Section 4 — Politique de confidentialité ─── */}
      <section id="confidentialite" className="px-5 mb-10 scroll-mt-4">
        <div className="t-cap mb-2">RGPD</div>
        <h2 className="t-ed-xl mb-3">Vos <em className="italic-keyword">données</em> personnelles</h2>
        <GoldenRule short />
        <p className="mt-3 t-mono-xs text-ink-3">Dernière mise à jour : 1er mai 2026. Conforme au RGPD (UE) 2016/679 et à la loi Informatique et Libertés du 6 janvier 1978 modifiée.</p>

        <ArticleTitle>3.1 — Responsable du traitement</ArticleTitle>
        <p className="t-ui text-ink-2">Jeremy Montesinos — contact : <a href="mailto:contact@gazette.fr" className="underline">contact@gazette.fr</a></p>
        <p className="mt-2 t-ui text-ink-2">Aucun Délégué à la Protection des Données (DPO) n'a été désigné à ce jour. Pour toute question relative à vos données personnelles, adressez-vous à contact@gazette.fr.</p>

        <ArticleTitle>3.2 — Données collectées</ArticleTitle>
        <p className="t-ui text-ink-2 font-medium">Données d'identification et de compte :</p>
        <BulletList items={[
          'adresse email (fournie lors de l\'inscription ou transmise par Google lors de la connexion OAuth) ;',
          'nom d\'affichage (dérivé automatiquement de votre compte Google ou de votre adresse email) ;',
          'date de création du compte.',
        ]} />
        <p className="mt-3 t-ui text-ink-2 font-medium">Données de préférences :</p>
        <BulletList items={[
          'ville de résidence et coordonnées géographiques associées (issues de la sélection dans le moteur de recherche Nominatim, pas de la géolocalisation GPS) ;',
          'catégories d\'événements préférées (marchés, musique, culture, etc.) ;',
          'publics visés préférés (famille, enfants, etc.) ;',
          'lieu de vacances (si le mode vacances est activé).',
        ]} />
        <p className="mt-3 t-ui text-ink-2 font-medium">Données d'utilisation du Service :</p>
        <BulletList items={[
          'liste des événements ajoutés en favoris ;',
          'alertes créées (critères géographiques, catégories, fréquence) ;',
          'abonnements à des organisateurs ou des villes ;',
          'préférence de thème visuel (jour / nuit / auto).',
        ]} />
        <p className="mt-3 t-ui text-ink-2 font-medium">Données techniques de session (gérées par Supabase Auth) :</p>
        <BulletList items={[
          'adresse IP ;',
          'user agent du navigateur ;',
          'jetons d\'authentification (session, refresh).',
        ]} />
        <p className="mt-3 t-ui text-ink-2 font-medium">Données optionnelles :</p>
        <BulletList items={[
          'jeton de notification push (si l\'utilisateur active les notifications) ;',
          'consentement notifications (oui/non).',
        ]} />

        <ArticleTitle>3.3 — Données que nous ne collectons pas</ArticleTitle>
        <BulletList items={[
          'Aucune donnée de géolocalisation GPS n\'est stockée en base de données. La géolocalisation du navigateur peut être utilisée ponctuellement pour centrer la carte, mais elle n\'est ni enregistrée ni transmise à des tiers.',
          'Aucun cookie publicitaire, pixel de suivi ou outil d\'analytics tiers n\'est intégré au Service.',
          'Aucune donnée bancaire ou de paiement n\'est collectée.',
          'Aucune donnée sensible au sens de l\'article 9 du RGPD (origine ethnique, opinions politiques, données de santé, etc.) n\'est collectée.',
        ]} />

        <ArticleTitle>3.4 — Finalités et bases légales du traitement</ArticleTitle>
        <DataBlocks3 rows={[
          { col1: 'Création et gestion de votre compte', col2: 'Exécution du contrat (art. 6.1.b RGPD)', col3: 'Email, nom, date de création' },
          { col1: 'Personnalisation des résultats (événements proches de votre ville)', col2: 'Exécution du contrat', col3: 'Ville, coordonnées, préférences' },
          { col1: 'Gestion des favoris, alertes et abonnements', col2: 'Exécution du contrat', col3: 'Favoris, alertes, follows' },
          { col1: 'Envoi de notifications push', col2: 'Consentement (art. 6.1.a RGPD)', col3: 'Jeton push, opt-in notifications' },
          { col1: 'Sécurité et prévention des abus', col2: 'Intérêt légitime (art. 6.1.f RGPD)', col3: 'IP, user agent, sessions' },
          { col1: 'Stockage de la préférence de thème visuel', col2: 'Intérêt légitime', col3: 'Clé localStorage' },
        ]} />

        <ArticleTitle>3.5 — Destinataires des données</ArticleTitle>
        <p className="t-ui text-ink-2">Vos données personnelles sont accessibles aux sous-traitants techniques suivants :</p>
        <BulletList items={[
          'Supabase Inc. (base de données, authentification, fonctions serveur) — données hébergées en région eu-central-1 (Francfort, UE) ;',
          'Vercel Inc. (hébergement du site web) — réseau mondial avec point de présence en UE ;',
          'wsrv.nl (proxy et optimisation d\'images) — ne reçoit aucune donnée personnelle, uniquement des URLs d\'images publiques ;',
          'Nominatim / OpenStreetMap (géocodage de la ville saisie) — reçoit uniquement le texte de recherche de ville, pas votre identité.',
        ]} />
        <p className="mt-3 t-ui text-ink-2 font-medium">Nous ne vendons, ne louons ni ne transmettons vos données personnelles à des tiers à des fins commerciales ou publicitaires.</p>

        <ArticleTitle>3.6 — Transferts hors Union européenne</ArticleTitle>
        <p className="t-ui text-ink-2">Supabase Inc. et Vercel Inc. sont des sociétés américaines. Les données sont hébergées principalement dans l'Union européenne (Supabase eu-central-1, Francfort). Certaines opérations techniques peuvent impliquer des transferts ponctuels vers les États-Unis, encadrés par le EU-US Data Privacy Framework (décision d'adéquation de la Commission européenne du 10 juillet 2023, sous réserve de sa validité en cours).</p>

        <ArticleTitle>3.7 — Durée de conservation</ArticleTitle>
        <DataBlocks2 rows={[
          { label: 'Compte et données associées', value: "Jusqu'à la suppression du compte par l'utilisateur" },
          { label: 'Sessions d\'authentification', value: "Expiration automatique gérée par Supabase (renouvellement continu tant que l'utilisateur est actif)" },
          { label: 'Adresse IP / user agent', value: 'Durée de la session, supprimés à l\'expiration' },
          { label: 'Événements passés en base', value: 'Purgés automatiquement 90 jours après leur date' },
          { label: 'Données après suppression de compte', value: 'Suppression immédiate et irréversible via delete_my_account (suppression en cascade)' },
        ]} />

        <ArticleTitle>3.8 — Vos droits</ArticleTitle>
        <p className="t-ui text-ink-2">Conformément au RGPD et à la Loi Informatique et Libertés, vous disposez des droits suivants :</p>
        <BulletList items={[
          'Droit d\'accès (art. 15 RGPD) : obtenir la confirmation que vos données sont traitées et en recevoir une copie.',
          'Droit de rectification (art. 16) : corriger vos données inexactes ou incomplètes. Vous pouvez modifier votre ville, vos préférences et votre nom d\'affichage directement dans l\'application.',
          'Droit à l\'effacement (art. 17) : supprimer votre compte et l\'ensemble de vos données. Cette opération est accessible directement depuis l\'application (Carnet → Supprimer mon compte) et est immédiate et irréversible.',
          'Droit à la portabilité (art. 20) : recevoir vos données dans un format structuré. Contactez-nous à contact@gazette.fr.',
          'Droit à la limitation du traitement (art. 18) et droit d\'opposition (art. 21) : contactez-nous à contact@gazette.fr.',
          'Droit de retirer votre consentement à tout moment pour les traitements fondés sur le consentement (notifications push), sans que cela ne remette en cause la licéité du traitement effectué avant le retrait.',
        ]} />
        <p className="mt-3 t-ui text-ink-2">Pour exercer vos droits : <a href="mailto:contact@gazette.fr" className="underline">contact@gazette.fr</a></p>

        <ArticleTitle>3.9 — Réclamation auprès de la CNIL</ArticleTitle>
        <p className="t-ui text-ink-2">Si vous estimez que le traitement de vos données constitue une violation du RGPD, vous pouvez introduire une réclamation auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) :</p>
        <p className="mt-2 t-ui text-ink-2">CNIL — 3, place de Fontenoy — TSA 80715 — 75334 Paris Cedex 07</p>
        <p className="t-ui text-ink-2"><a href="https://www.cnil.fr/fr/plaintes" target="_blank" rel="noreferrer" className="underline">cnil.fr/fr/plaintes</a></p>

        <ArticleTitle>3.10 — Cookies et stockage local</ArticleTitle>
        <p className="t-ui text-ink-2">Le Service n'utilise aucun cookie publicitaire ni cookie tiers.</p>
        <DataBlocks4 rows={[
          { col1: 'Session d\'authentification Supabase', col2: 'localStorage', col3: 'Maintien de la connexion', col4: 'Jusqu\'à déconnexion' },
          { col1: 'Préférence de thème (jour/nuit/auto)', col2: 'localStorage (clé lafete:theme)', col3: 'Confort d\'affichage', col4: 'Indéfinie' },
          { col1: 'Cache de l\'interface (shell applicatif)', col2: 'Service Worker Cache (lafete-shell-v1)', col3: 'Performance et accès hors ligne', col4: "Jusqu'à mise à jour de l'app" },
        ]} />
        <p className="mt-3 t-ui text-ink-2">Ces stockages sont strictement nécessaires au fonctionnement du Service et sont exemptés du recueil de consentement conformément aux lignes directrices de la CNIL (délibération n° 2020-091 du 17 septembre 2020).</p>

        <ArticleTitle>3.11 — Sécurité</ArticleTitle>
        <p className="t-ui text-ink-2">Les communications entre votre navigateur et nos serveurs sont chiffrées via HTTPS/TLS. L'accès aux données en base est protégé par des politiques de sécurité au niveau des lignes (Row Level Security) garantissant que chaque utilisateur n'accède qu'à ses propres données. Les mots de passe ne sont pas stockés par notre Service (authentification par lien magique ou OAuth Google).</p>

        <ArticleTitle>3.12 — Modification de la politique</ArticleTitle>
        <p className="t-ui text-ink-2">La présente politique peut être mise à jour à tout moment. La date de dernière mise à jour est indiquée en tête de document. Nous vous invitons à la consulter régulièrement.</p>
      </section>

      {/* ─── Section 5 — Notice & Takedown ─── */}
      <section className="px-5 mb-10">
        <div className="t-cap mb-2">Signalement</div>
        <h2 className="t-ed-xl mb-3">Retrait d'un <em className="italic-keyword">événement</em></h2>
        <GoldenRule short />
        <p className="mt-4 t-ui text-ink-2">Si vous êtes organisateur d'un événement référencé sur Gazette et souhaitez en demander la modification ou le retrait, contactez-nous à <a href="mailto:contact@gazette.fr" className="underline">contact@gazette.fr</a> en précisant :</p>
        <BulletList items={[
          'l\'événement concerné (titre, date, lieu) ;',
          'la nature de votre demande (correction, retrait) ;',
          'votre qualité (organisateur, lieu, collectivité).',
        ]} />
        <p className="mt-3 t-ui text-ink-2">Nous nous engageons à traiter votre demande sous <strong>72 heures ouvrées</strong>.</p>
      </section>

      <footer className="px-5 mt-10 mb-6 flex flex-col items-center gap-2 text-center t-mono-xs text-ink-3">
        <BrandLogo size={28} className="text-ink-3" />
        <span>Édition Française · {new Date().getFullYear()}</span>
      </footer>
    </div>
  );
}
